import React, {
  useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import LoadingBar from '../../../nucleus/loading-bar/loading-bar';
import AllocationsReview from './AllocationsReview';
import BillToReview from './BillToReview';
import CommissionsReview from './CommissionsReview';
import CompDataReview from './CompDataReview';
import CompanyContactReview from './CompanyContactReview';
import ConsiderationReview from './ConsiderationReview';
import FutureActionsReview from './FutureActionsReview';
import GettingStartedReview from './GettingStartedReview';
import InstallmentsReview from './InstallmentsReview';
import InvestmentDetailsReview from './InvestmentDetailsReview';
import PropertyReview from './PropertyReview';
import RequiredFieldsSummary from './RequiredFieldsSummary';
import { RequiredFieldsProvider } from './RequiredFieldsContext';
import { useReviewEditTracker } from './ReviewEditTrackingContext';
import ReviewEditTrackingProvider from './ReviewEditTrackingProvider';
import { buildEditEvents } from './review-edit-events';
import { createLinks } from '../BottomNav';
import { updateDeal } from '../../../redux/actions/deal-actions';
import {
  fetchCommercialProperty, ensureCommercialProperty, updateVoucher, updateCommercialProperty, updateCompany, setUpdatedCompanyInfo,
} from '../../../redux/actions/voucher/voucher-actions';
import { fetchCompData, updateCompData, createCompData } from '../../../redux/actions/voucher/comp-data-actions';
import { updateDealContact } from '../../../redux/actions/deal-contacts-actions';
import { fetchConsiderations } from '../../../redux/actions/considerations-actions';
import { fetchFiles, fetchVoucherFiles, updateFile } from '../../../redux/actions/deal-files-actions';
import verificationService from '../../../services/verification-service';
import { toast } from '../../../redux/actions/toasts-actions';
import { fetchFinancials } from '../../../redux/actions/financial-actions';
import { subscribeToDealExtractionStatus } from '../../../services/attachments-service';
import vouchersService from '../../../services/voucher/vouchers-service';
import companiesService, { defaultCompanyData } from '../../../services/companies-service';
import * as compDataService from '../../../services/voucher/comp-data-service';
import { fetchBrokerVerificationBounds, DEFAULT_BOUNDS } from '../../../services/voucher/broker-verification-service';
import {
  setBrokerVerificationBounds as publishBrokerVerificationBounds,
  setBrokerVerificationBoundsFailed,
} from '../../../redux/actions/voucher/broker-verification-actions';
import useVerificationRequired from '../../../hooks/useVerificationRequired';
import { trackVerificationMarket } from '../../../services/broker-verification-market-tracking-service';
import considerationsService from '../../../services/voucher/considerations-service';
import BannerNotice from '../../../nucleus/notices/banner-notice';
import isEnhancedLeaseAbstractionDeal from '../../../utils/lease-abstraction-utils';
import { isBrokerVerificationActive } from '../../../utils/broker-verification-utils';
import { getCommissionAttachmentCategory, getCommissionAttachmentFiles } from '../../../utils/commission-agreement-utils';

// Returns true only when the extracted chip value meaningfully differs from the live DB value.
const fieldValuesAreDifferent = (extracted, current) => {
  if (!extracted || String(extracted).trim() === '') return false;
  const a = String(extracted).trim().toLowerCase();
  const b = String(current ?? '').trim().toLowerCase();
  if (a === b) return false;
  const falsyWords = new Set(['false', 'no', 'unknown', '0', '']);
  const truthyWords = new Set(['true', 'yes', '1']);
  if (falsyWords.has(a) && falsyWords.has(b)) return false;
  if (truthyWords.has(a) && truthyWords.has(b)) return false;
  const numA = parseFloat(a);
  const numB = parseFloat(b);
  if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA !== numB;
  return true;
};

// Reports each committed panel edit (broker_verification_field_edited) before delegating to the
// real save. The Redux snapshot is read here, BEFORE dispatch, so "previous" values are pre-edit.
const TrackedRequiredFieldsSummary = ({ onEditField, onContinue }) => {
  const trackEdit = useReviewEditTracker();
  const store = useStore();
  const { status, verificationRequired } = useVerificationRequired();

  const handleEditFieldWithTracking = (entry, newValue) => {
    try {
      const state = store.getState();
      const snapshot = {
        deal: state.deal.deal,
        voucher: state.voucher.voucher,
        compData: state.compData,
        commercialProperties: state.voucher.commercialProperties,
        contacts: [state.dealContacts?.primaryContact, ...(state.dealContacts?.contacts || [])].filter(Boolean),
      };
      buildEditEvents(entry, newValue, snapshot).forEach(edit => trackEdit && trackEdit(edit));
    } catch (error) {
      console.error('Failed to track broker verification field edit');
    }
    return onEditField(entry, newValue);
  };

  return (
    <RequiredFieldsSummary
      onEditField={handleEditFieldWithTracking}
      onContinue={onContinue}
      verificationRequired={verificationRequired}
      verificationLoading={status === 'loading'}
    />
  );
};

TrackedRequiredFieldsSummary.propTypes = {
  onEditField: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
};

const Review = ({ onVoucherValidChange = () => {} }) => {
  const history = useHistory();
  const location = useLocation();
  const reduxStore = useStore();
  const {
    deal, dealType, voucher, isEnhancedFlow, abstractionLeaseFile, dealFiles, commercialProperties, compData, featureFlags, repRole,
  } = useSelector(state => ({
    deal: state.deal.deal,
    dealType: state.deal.deal?.deal_type,
    voucher: state.voucher.voucher,
    isEnhancedFlow: isEnhancedLeaseAbstractionDeal(state.deal.deal),
    abstractionLeaseFile: state.dealFiles?.abstractionLeaseFile,
    dealFiles: state.dealFiles?.files,
    commercialProperties: state.voucher.commercialProperties,
    compData: state.compData,
    featureFlags: state.userOptions.userOptions.feature_flags || {},
    repRole: state.deal.deal?.rep_role,
  }));

  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);

  const [isVoucherValid, setIsVoucherValid] = useState(true);
  const isVoucherValidRef = useRef(null);
  const [extractionData, setExtractionData] = useState(null);
  // null = not computed yet, {} = computed with no diffs, { leaseDetails, financials, leaseComp } = computed with diffs
  const [extractionAlternatives, setExtractionAlternatives] = useState(null);

  // broker-verification bounds/required-fields (BR-F8, BR-F9): default empty shape when
  // the feature flag is off or before the first fetch resolves.
  const brokerVerificationFlagOn = isBrokerVerificationActive(featureFlags, voucher.is_early_invoice);
  // Edit metrics only exist for the broker-verification panel, so without the flag there is no
  // tracking context at all (every tracking hook in shared components stays a no-op).
  const MetricsBoundary = brokerVerificationFlagOn ? ReviewEditTrackingProvider : React.Fragment;
  const [brokerVerificationBounds, setBrokerVerificationBounds] = useState(DEFAULT_BOUNDS);
  // Tracks the edp_property_id bounds were last fetched with, so the property-change
  // effect below only re-fetches on a genuine change instead of duplicating the mount fetch.
  const lastFetchedEdpPropertyIdRef = useRef(undefined);
  // Guards the property-change effect until the mount effect's own bounds fetch has fully
  // settled. Comparing edp_property_id alone races: the Redux update from the mount fetch's
  // dispatch can trigger this effect before lastFetchedEdpPropertyIdRef is set, firing a
  // duplicate request. A boolean flip after the mount fetch settles has no such ordering risk.
  const initialBoundsFetchSettledRef = useRef(false);

  // Persist dismiss per deal in sessionStorage so it survives tab navigation
  // within the same browser session but resets on page refresh / new deal.
  const dismissKey = `review_banner_dismissed_${deal?.id}`;
  const [alternativesBannerDismissed, setAlternativesBannerDismissed] = useState(sessionStorage.getItem(dismissKey) === 'true');
  const dismissBanner = () => {
    sessionStorage.setItem(dismissKey, 'true');
    setAlternativesBannerDismissed(true);
  };

  // Reset before each render so children write fresh values during this pass.
  if (!isLoading) {
    localStorage.setItem('voucher_valid', JSON.stringify(true));
  }

  // After every render, read back the final localStorage value that children
  // wrote during this render pass and sync it into state. useLayoutEffect
  // fires before the browser paints, so there is no visible flicker.
  // The Promise.all loading gate ensures all data is ready before this runs,
  // preventing false validation errors from empty Redux state.
  // No dep array — self-healing if any child still lags; React bails out on
  // same-value setState so this converges immediately without infinite loops.
  // The review sections mark voucher_valid during render. This effect must
  // run after every review render to sync the final child-written value.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    if (!isLoading) {
      const nextIsVoucherValid = JSON.parse(localStorage.getItem('voucher_valid')) === true;
      localStorage.setItem('voucher_valid', JSON.stringify(nextIsVoucherValid));
      if (isVoucherValidRef.current !== nextIsVoucherValid) {
        isVoucherValidRef.current = nextIsVoucherValid;
        setIsVoucherValid(nextIsVoucherValid);
        onVoucherValidChange(nextIsVoucherValid);
      }
    }
  });

  useEffect(() => {
    // Fetch all data in parallel and hide the loading bar only when everything
    // is ready — same pattern as Angular's forkJoin(...).subscribe(() => done).
    // This guarantees no review section renders with empty Redux data, which
    // would cause false "required" errors and an incorrect validation banner.
    // broker-verification bounds fetch is folded into this same Promise.all (BR-F9,
    // Q8) so it shares the single loading gate; feature-flag-off means zero additional
    // network requests at all (BR-F8). Waits on the commercial property fetch below so
    // this single request already carries the real edp_property_id (BR-4) instead of
    // firing a throwaway parameterless request first.
    // Lease/sale vouchers need a property to ask for its address; early invoices don't require one.
    const needsProperty = (dealType === 'lease' || dealType === 'sale') && !voucher.is_early_invoice;
    const commercialPropertyFetch = dispatch(needsProperty ? ensureCommercialProperty(deal.id) : fetchCommercialProperty(deal.id));
    let resolvedBounds = DEFAULT_BOUNDS;
    let resolvedEdpPropertyId;
    let marketReported = false;
    const reportMarket = () => {
      if (!brokerVerificationFlagOn || marketReported) return;
      marketReported = true;
      trackVerificationMarket({
        dealId: deal.id, edpPropertyId: resolvedEdpPropertyId, market: resolvedBounds?.baseRent?.market,
      });
    };
    const brokerVerificationFetch = brokerVerificationFlagOn
      ? commercialPropertyFetch.then((action) => {
        const primary = (action?.payload || []).find(p => p.location_type_new !== false) || null;
        const edpPropertyId = primary?.edp_property_id;
        resolvedEdpPropertyId = edpPropertyId;
        lastFetchedEdpPropertyIdRef.current = edpPropertyId;
        return fetchBrokerVerificationBounds({ edpPropertyId });
      })
      : Promise.resolve(DEFAULT_BOUNDS);

    Promise.all([
      // Same create-if-missing fallback as CompDataTab.js's own mount effect: a deal that
      // hasn't been through the Lease/Sale Comp tab yet (or an early-invoice voucher that
      // skips it entirely) has zero InternalComp rows at this point, so `state.compData`
      // resolves to `[]` and `compData.id` is `undefined`. Without this, inline-editing any
      // Comp Data field from the new "Before you submit" panel (e.g. Free Rent) dispatched
      // `updateCompData(undefined, ...)`, PATCHing `/mbb/voucher/internalcomp/undefined` and
      // failing outright instead of saving.
      dispatch(fetchCompData({ deal: deal.id })).then((result) => {
        if (result.payload.length === 0) {
          return dispatch(createCompData({
            deal: deal.id,
            [`${repRole}_represented_by`]: 'CBRE',
            ...(dealType !== 'sale' && !isEnhancedFlow && { rent_escalation_is_custom: false }),
          }));
        }
        return result;
      }),
      commercialPropertyFetch,
      dispatch(fetchConsiderations(deal.id)),
      dispatch(fetchFiles({
        queryParams: { ordering: '-modified', page: 1, page_size: 25 },
        deal: deal.id,
      })),
      dispatch(fetchFinancials(deal.id, dealType)),
      brokerVerificationFetch.then((bounds) => {
        resolvedBounds = bounds;
        setBrokerVerificationBounds(bounds);
      }),
    ]).then(() => {
      initialBoundsFetchSettledRef.current = true;
      // Published only once every decision input is loaded, so nothing routes on partial data.
      if (brokerVerificationFlagOn) {
        dispatch(publishBrokerVerificationBounds(voucher.id, resolvedBounds));
        reportMarket();
      }
      setIsLoading(false);
    })
      .catch(() => {
        initialBoundsFetchSettledRef.current = true;
        if (brokerVerificationFlagOn) dispatch(setBrokerVerificationBoundsFailed(voucher.id));
        reportMarket();
        setIsLoading(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal.id, dealType, dispatch]);

  const primaryProperty = (commercialProperties || []).find(p => p.location_type_new !== false) || null;

  // Next-page URL for the "Before you submit" panel's own CONTINUE button (mirrors the
  // exact same routing logic already used by the persistent `BottomNav` at the bottom of
  // the page), so clicking it navigates the same way as the global next/continue link.
  // Same decision the tab bar and bottom nav use, so Continue always lands where they do.
  const { status: verificationStatus, verificationRequired } = useVerificationRequired();
  const { next } = createLinks(dealType, location.pathname, voucher.is_early_invoice, verificationRequired);
  const handleContinue = () => {
    if (verificationStatus === 'loading') return;
    if (next?.url) history.push(next.url);
  };

  // Re-fetch broker-verification bounds only when the primary commercial property's EDP id
  // changes while the Review tab is mounted (Algorithm 2, BR-F9) — never on a per-keystroke/
  // per-field-edit basis. Waits until the mount effect's own bounds fetch has settled so a
  // Redux update landing mid-mount can never race it into firing a duplicate request.
  // property_type is resolved server-side from EDP itself (not sent by the client — BR-4).
  useEffect(() => {
    if (!brokerVerificationFlagOn) return;
    if (!initialBoundsFetchSettledRef.current) return;
    const edpPropertyId = primaryProperty?.edp_property_id;
    if (edpPropertyId === lastFetchedEdpPropertyIdRef.current) return;
    lastFetchedEdpPropertyIdRef.current = edpPropertyId;
    fetchBrokerVerificationBounds({ edpPropertyId }).then((bounds) => {
      setBrokerVerificationBounds(bounds);
      dispatch(publishBrokerVerificationBounds(voucher.id, bounds));
      trackVerificationMarket({ dealId: deal.id, edpPropertyId, market: bounds?.baseRent?.market });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryProperty?.edp_property_id, brokerVerificationFlagOn]);

  // Client-side dispatch target shared by every registered required-field entry
  // (FR-9). Required-field detection is now fully client-side via
  // `RequiredFieldsContext` — each `RequiredLabeledItem` instance registers/
  // unregisters itself using its own existing `showError` computation, so no
  // separate "is voucher valid" check is needed here; the existing inline
  // `localStorage.setItem('voucher_valid', ...)` calls already cover that.
  const handleEditField = (entry, newValue) => {
    const {
      reduxAction, fieldPath, targetId, spaceIndex, phoneNumbers, emailAddresses, dealFieldPath,
    } = entry.editTarget || {};
    if (!reduxAction || !fieldPath) return undefined;
    switch (reduxAction) {
      case 'updateDeal':
        // "Client Code - Description" (GettingStartedReview.js) commits its selected
        // ClientCodeAutocomplete result as a single `{code, description}` value (see
        // RequiredFieldsSummary.js's 'clientCode' controlType) -- split it back into the
        // two real deal fields it actually maps to, matching GettingStartedTab.js's own
        // `handleClientSelected`.
        if (fieldPath === 'account_code_description' && newValue && typeof newValue === 'object') {
          return dispatch(updateDeal(deal.id, {
            account_code: newValue.code,
            account_description: newValue.description,
          }));
        }
        // Company Name search (ReviewCompanyNameSearchControl) commits the full
        // newly-created/selected company record, not just its id -- sync it into
        // state.voucher.updatedInfo so CompanyContactReview.js's own `updatedCompanyInfo`
        // effect can use it directly instead of re-fetching a company it was just given
        // (2026-08-11: reported as an extra GET call not present in the live voucher flow).
        if (newValue && typeof newValue === 'object' && newValue.id) {
          dispatch(setUpdatedCompanyInfo(newValue));
          return dispatch(updateDeal(deal.id, { [fieldPath]: newValue.id }));
        }
        return dispatch(updateDeal(deal.id, { [fieldPath]: newValue }));
      case 'updateCompData':
        if (!compData?.id) return undefined;
        if (fieldPath === 'rent_escalation_per_year' && newValue && typeof newValue === 'object') {
          return dispatch(updateCompData(compData.id, newValue));
        }
        return dispatch(updateCompData(compData.id, { [fieldPath]: newValue }));
      case 'updateVoucher':
        return dispatch(updateVoucher(voucher.id, { [fieldPath]: newValue }));
      // Patches a single field directly on one commercial property, identified by its id
      // (targetId) — used by per-property required fields such as Space Type or Square
      // Footage, where multiple properties may each have their own registry entry.
      case 'updateCommercialProperty':
        if (targetId) {
          if (entry.editTarget?.enablePropertyLookup && newValue && typeof newValue === 'object') {
            return dispatch(updateCommercialProperty(targetId, newValue));
          }
          return dispatch(updateCommercialProperty(targetId, { [fieldPath]: newValue }));
        }
        return undefined;
      // Patches a single field on one company record, identified by its id (targetId) --
      // used by per-company required fields (Tenant/Landlord/Seller/Buyer address, etc.),
      // where multiple companies may each have their own registry entry.
      case 'updateCompany': {
        const companyFieldPayload =
          fieldPath === 'voucher_naics_choice' && newValue && typeof newValue === 'object'
            ? newValue
            : { [fieldPath]: newValue };
        if (targetId) {
          const companyUpdateResult = dispatch(updateCompany(targetId, companyFieldPayload));
          if (!deal?.id || !dealFieldPath) {
            return companyUpdateResult;
          }
          return Promise.resolve(companyUpdateResult).then((updatedCompany) => {
            const resolvedCompanyId = updatedCompany?.id || targetId;
            if (!resolvedCompanyId) return updatedCompany;
            return Promise.resolve(dispatch(updateDeal(deal.id, { [dealFieldPath]: resolvedCompanyId })))
              .then(() => updatedCompany);
          });
        }
        // No company exists yet for this party (e.g. Address/City/State/Zip filled in before
        // Company Name) -- mirror the live voucher flow's useCompanyState `onCompanyBlur`,
        // which creates a brand new company from whichever field was filled in first instead
        // of silently no-op'ing (2026-08-13 bug report: "landlord fields are not updating").
        if (!deal?.id || !dealFieldPath) return undefined;
        return companiesService.createCompany({ ...defaultCompanyData, ...companyFieldPayload })
          .then((newCompany) => {
            dispatch(setUpdatedCompanyInfo(newCompany));
            return Promise.resolve(dispatch(updateDeal(deal.id, { [dealFieldPath]: newCompany.id })))
              .then(() => newCompany);
          });
      }
      // Patches a single deal contact (Point of Contact), identified by its id (targetId).
      // `given_name`/`surname` are flat fields, but `phone`/`email` are stored as the first
      // entry of a `phone_numbers`/`email_addresses` array on the contact -- the existing
      // array (passed through via editTarget.phoneNumbers/emailAddresses) is preserved and
      // only the first entry's number/address is replaced, matching how VoucherContactForm
      // patches contacts.
      case 'updateDealContact': {
        if (!targetId) return undefined;
        let payload;
        if (fieldPath === 'phone') {
          const existingPhones = phoneNumbers && phoneNumbers.length ? phoneNumbers : [{ type: 'work' }];
          payload = { phone_numbers: existingPhones.map((p, i) => (i === 0 ? { ...p, number: newValue } : p)) };
        } else if (fieldPath === 'email') {
          const existingEmails = emailAddresses && emailAddresses.length ? emailAddresses : [{ type: 'work' }];
          payload = { email_addresses: existingEmails.map((e, i) => (i === 0 ? { ...e, address: newValue } : e)) };
        } else if (newValue && typeof newValue === 'object') {
          // A full contact was picked from the Point of Contact search results (First/Last
          // Name's `contactSearch` controlType, matching VoucherContactForm's `nameOnChange`)
          // -- copy the selected contact's name/phone/email/job title onto the CURRENT
          // contact record (targetId), rather than switching this row to the selected
          // contact's own id.
          payload = {
            given_name: newValue.given_name || '',
            surname: newValue.surname || '',
            phone_numbers: (newValue.phone_numbers || []).map(p => ({ ...p })),
            email_addresses: (newValue.email_addresses || []).map(e => ({ ...e })),
            job_title: newValue.job_title || '',
          };
        } else {
          payload = { [fieldPath]: newValue };
        }
        return dispatch(updateDealContact(targetId, { ...payload, display_toast: false }));
      }
      // Patches a single field on one space within one commercial property, identified by
      // targetId (property id) + spaceIndex — used by per-suite required fields.
      case 'updateSpace': {
        const targetProperty = (commercialProperties || []).find(p => p.id === targetId);
        if (targetProperty?.spaces?.length > spaceIndex) {
          const updatedSpaces = targetProperty.spaces.map((space, index) => (
            index === spaceIndex ? { ...space, [fieldPath]: newValue } : space
          ));
          return dispatch(updateCommercialProperty(targetId, { spaces: updatedSpaces }));
        }
        return undefined;
      }
      // The documents are the voucher's files tagged for the current answer (Yes: CA, No: OTH);
      // selecting one tags it and adds it to the voucher, removing one clears the tag (as Getting Started).
      case 'updateCommissionAttachments': {
        const targetCategory = getCommissionAttachmentCategory(deal?.has_commission_agreement);
        if (!deal?.id || !targetCategory) return undefined;
        const readFiles = () => reduxStore.getState().dealFiles?.files || [];
        const findFile = id => readFiles().find(file => parseInt(file.id, 10) === id);
        const selectedIds = (newValue || []).map(id => parseInt(id, 10)).filter(Number.isFinite);
        const currentIds = getCommissionAttachmentFiles(readFiles(), deal.has_commission_agreement)
          .map(file => parseInt(file.id, 10));
        const added = selectedIds.filter(id => !currentIds.includes(id));
        const removed = currentIds.filter(id => !selectedIds.includes(id));
        if (!added.length && !removed.length) return undefined;
        // A file just uploaded from the panel, or one outside the loaded page, is not in Redux yet;
        // look it up in the same list the selector shows.
        const lookup = added.some(id => !findFile(id))
          ? Promise.resolve(verificationService.fetchVerificationAttachments(deal.id)).catch(() => [])
          : Promise.resolve([]);
        return lookup.then((fetched) => {
          const fileFor = id => findFile(id) || (Array.isArray(fetched) ? fetched : [])
            .find(file => parseInt(file.id, 10) === id);
          const changes = [
            ...added.map(id => ({ file: fileFor(id), category: targetCategory })),
            ...removed.map(id => ({ file: fileFor(id), category: null })),
          ];
          if (changes.some(({ file }) => !file)) {
            dispatch(toast('Unable to update commission agreement selection. Please try again.'));
            return undefined;
          }
          const needsReload = changes.some(({ file }) => !findFile(file.id));
          const addsToVoucher = changes.some(({ file }) => file.is_lease_document !== true);
          return Promise.all(changes
            .filter(({ file, category }) => (file.document_category || null) !== category || file.is_lease_document !== true)
            .map(({ file, category }) => dispatch(updateFile(file.id, {
              ...file,
              is_lease_document: true,
              document_category: category,
            }))))
            .then((results) => {
              // The voucher list under Getting Started only catches up from the server.
              if (addsToVoucher) {
                dispatch(fetchVoucherFiles({
                  deal: deal.id, ordering: '-modified', page: 1, page_size: 25,
                }));
              }
              // Files known only from the lookup are not in Redux, so reload for the panel to see them.
              if (needsReload) {
                dispatch(fetchFiles({
                  queryParams: { ordering: '-modified', page: 1, page_size: 25 },
                  deal: deal.id,
                }));
              }
              return results;
            });
        });
      }
      default:
        return undefined;
    }
  };


  // Resolve the active lease file using the same priority as other abstraction tabs:
  // 1. Redux abstractionLeaseFile  2. localStorage  3. is_lease_document  4. first file
  const filesLoaded = dealFiles && dealFiles.length > 0;
  const resolvedLeaseFile = (() => {
    if (!isEnhancedFlow) return null;
    const isValidRef = f => f && f.id && filesLoaded && dealFiles.some(df => df.id === f.id);
    if (isValidRef(abstractionLeaseFile)) return abstractionLeaseFile;
    try {
      const stored = localStorage.getItem(`abstractionVoucherFile_${deal?.id}`);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && parsed.id) {
          if (!filesLoaded || dealFiles.some(df => df.id === parsed.id)) return parsed;
          localStorage.removeItem(`abstractionVoucherFile_${deal?.id}`);
        }
      }
    } catch (e) { /* ignore */ }
    if (filesLoaded) {
      return dealFiles.find(f => f.is_lease_document === true) || dealFiles[0] || null;
    }
    return null;
  })();

  // Keep extraction data in sync using the shared deal poller subscription.
  useEffect(() => {
    if (!isEnhancedFlow || !resolvedLeaseFile?.id || !deal?.id) return undefined;
    setExtractionData(null);
    setExtractionAlternatives(null);

    const fileId = resolvedLeaseFile.id;
    const unsubscribe = subscribeToDealExtractionStatus({
      dealId: deal.id,
      fileId,
      onUpdate: (result) => {
        const data = result?.data_extract || null;
        setExtractionData(data);
      },
      onError: () => {},
    });

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnhancedFlow, resolvedLeaseFile?.id, deal?.id]);

  // Compute which tabs have differing values by fetching fresh DB data directly via
  // services — never from Redux, which other tabs may have already modified.
  useEffect(() => {
    if (!isEnhancedFlow || !extractionData || !deal?.id) return;
    Promise.all([
      vouchersService.fetchCommercialProperty(deal.id),
      compDataService.fetchCompData({ deal: deal.id }),
      considerationsService.fetchConsiderations(deal.id),
    ]).then(([cpRes, cdRes, consRes]) => {
      const cp = (cpRes?.results || []).find(p => p.location_type_new === true)
        || (cpRes?.results || [])[0];
      const space = cp?.spaces?.[0];
      const freshCompData = cdRes && !Array.isArray(cdRes) ? cdRes : {};
      const existingConsiderations = consRes || [];

      const chipVal = field => field?.value || field?.extracted_value || '';
      const differs = (field, dbVal) => fieldValuesAreDifferent(chipVal(field), dbVal != null ? String(dbVal) : '');

      // ── Lease Details ──
      const li = extractionData.lease_info || {};
      const leaseDetails = [
        [li.lease_start_date, deal.date_lease_from],
        [li.lease_expiration_date, voucher?.lease_expiration_date],
        [li.lease_execution_date, deal.date_lease_execution],
        [li.lease_occupancy_date, deal.date_lease_occupancy],
        [li.property_name, cp?.name],
        [li.address, cp?.address],
        [li.city, cp?.city],
        [li.state_province, cp?.state_abbreviation],
        [li.zip, cp?.postal_code],
        [li.square_footage, cp?.building_total_square_feet],
        [li.suite, space?.address_line2],
      ].some(([field, db]) => differs(field, db));

      // ── Financials ──
      const rs = extractionData.consideration_info?.rent_schedule;
      let extractedRows = null;
      if (Array.isArray(rs?.extracted_value) && rs.extracted_value.length > 0) {
        extractedRows = rs.extracted_value;
      } else if (Array.isArray(rs?.value) && rs.value.length > 0) {
        extractedRows = rs.value;
      }
      const financials = !!(extractedRows && extractedRows.length !== existingConsiderations.length);

      // ── Lease Comp ──
      const ci = extractionData.comp_info || {};
      const leaseComp = [
        [ci.tenant_represented_by, freshCompData.tenant_represented_by],
        [ci.landlord_represented_by, freshCompData.landlord_represented_by],
        [ci.base_rent, freshCompData.base_rent],
        [ci.free_rent, freshCompData.free_rent],
        [ci.parking_ratio, freshCompData.parking_ratio],
        [ci.tenant_improvements_detail, freshCompData.tenant_improvements_detail],
      ].some(([field, db]) => differs(field, db));

      setExtractionAlternatives({ leaseDetails, financials, leaseComp });
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extractionData, deal?.id]);

  const displayCompanyContactReview = () => {
    switch (dealType) {
      case 'sale':
        return (
          <>
            <CompanyContactReview partyType="buyer" />
            <CompanyContactReview partyType="seller" />
          </>
        );
      case 'lease':
        return (
          <>
            <CompanyContactReview partyType="tenant" />
            <CompanyContactReview partyType="landlord" />
          </>
        );
      case 'consulting':
        return <CompanyContactReview partyType="consulting" />;
      case 'other':
        return <CompanyContactReview partyType="other" />;
      default:
        return '';
    }
  };

  return (
    <div className="review-tab">
      {isLoading && <LoadingBar isLoading />}
      {isLoading
        ? null
        : (
          <>
            {!isVoucherValid && <BannerNotice iconClassName="warning" mainMessage="Enter all required fields including Comp fields before continuing" />}
            {isEnhancedFlow && extractionAlternatives
              && (extractionAlternatives.leaseDetails || extractionAlternatives.financials || extractionAlternatives.leaseComp)
              && !alternativesBannerDismissed && (() => {
                const tabNames = [
                  extractionAlternatives.leaseDetails && 'Lease Details',
                  extractionAlternatives.financials && 'Financials',
                  extractionAlternatives.leaseComp && 'Lease Comp',
                ].filter(Boolean).join(', ');
                let firstUrl = `/voucher/${deal.id}/comp_data`;
                if (extractionAlternatives.leaseDetails) firstUrl = `/voucher/${deal.id}/voucher_details/lease_information`;
                else if (extractionAlternatives.financials) firstUrl = `/voucher/${deal.id}/financials`;
                return (
                  <div className="extraction-alternatives-banner">
                    <span className="extraction-alternatives-banner__icon" aria-hidden="true" />
                    <span className="extraction-alternatives-banner__text">
                      Alternative Values Found in {tabNames}.{' '}
                      <button
                        type="button"
                        className="extraction-alternatives-banner__link"
                        onClick={() => history.push(firstUrl)}
                      >
                        Click here to review.
                      </button>
                    </span>
                    <button
                      type="button"
                      className="extraction-alternatives-banner__dismiss"
                      aria-label="Dismiss"
                      onClick={dismissBanner}
                    >
                      ×
                    </button>
                  </div>
                );
              })()}
            <h2>Review {voucher.is_early_invoice ? 'Early Invoice Request' : 'Voucher'}</h2>
            <p>Look over information and make sure it&apos;s accurate. Red means that you should take a second look.</p>
            <MetricsBoundary>
              <RequiredFieldsProvider>
                {brokerVerificationFlagOn && (
                  <TrackedRequiredFieldsSummary onEditField={handleEditField} onContinue={handleContinue} />
                )}
                <GettingStartedReview />
                <PropertyReview fieldBounds={brokerVerificationBounds.fieldBounds} />
                {dealType === 'sale' && <InvestmentDetailsReview />}
                {displayCompanyContactReview()}
                <ConsiderationReview />
                <AllocationsReview />
                <CommissionsReview />
                <InstallmentsReview />
                <BillToReview brokerVerificationEnabled={brokerVerificationFlagOn} />
                <FutureActionsReview />
                {(dealType === 'lease' || dealType === 'sale') && (
                  <CompDataReview
                    fieldBounds={brokerVerificationBounds.fieldBounds}
                    baseRent={brokerVerificationBounds.baseRent}
                  />
                )}
              </RequiredFieldsProvider>
            </MetricsBoundary>
          </>
        )
      }
    </div>
  );
};

Review.propTypes = {
  onVoucherValidChange: PropTypes.func,
};

Review.defaultProps = {
  onVoucherValidChange: () => {},
};

export default Review;
